// These types should ideally mirror backend DTOs/entities.

export enum AIActionType {
  CREATE_FILE = 'create_file',
  OVERWRITE_FILE = 'overwrite_file',
  DELETE_FILE = 'delete_file',
  QUICK_EDIT = 'quick_edit',
  APPLY_DIFF = 'apply_diff',
  PATCH = 'patch',
  RUN_COMMAND = 'run_command',
  EXECUTE_CODE = 'execute_code',
  REQUEST_CONTEXT = 'request_context',
  WRITE_TODO = 'write_todo',
  FINAL = 'final',
  ASK_USER = 'ask_user',
  INVOKE_SUBAGENT = 'invoke_subagent',
  LIST_SUB_AGENTS = 'list_sub_agents',
  GET_MESSAGES = 'get_messages',
  DISCARD_MESSAGES = 'discard_messages',
  SESSION_STATUS = 'session_status',
  HTML_FINAL = 'html_final',
  MANAGE_SYSTEM_PROMPTS = 'manage_system_prompts',
  MANAGE_CONTEXT_TEMPLATES = 'manage_context_templates',
  MANAGE_CONTEXT_SNIPPETS = 'manage_context_snippets',
  MANAGE_CUSTOM_SNIPPETS = 'manage_custom_snippets',
  MANAGE_CUSTOM_VARIABLES = 'manage_custom_variables',
  MANAGE_AGENTS = 'manage_agents',
  MANAGE_SUB_AGENTS = 'manage_sub_agents',
  MANAGE_MCP_SERVERS = 'manage_mcp_servers',
  MANAGE_TOOL_HOOKS = 'manage_tool_hooks',
  WEB_SEARCH = 'web_search',
  WEB_FETCH = 'web_fetch',
  REPLACE_MESSAGE_GROUPS = 'replace_message_groups',
  LIST_MCP = 'list_mcp',
  LIST_TOOLS = 'list_tools',
  LOAD_TOOL = 'load_tool',
  GENERATE_TITLE = 'generate_title',
}

export interface ExecutionLog {
  id: string; // log_id
  created_at: Date;
  updated_at: Date;
  action_id: string;
  output: string | null;
  error_message: string | null;
}

export interface AIAction {
  id: string;
  created_at: Date;
  updated_at: Date;
  input_id: string;
  action_type: AIActionType;
  file_path: string | null;
  content: string | null;
  original_content_for_revert: string | null;
  command_string: string | null;
  status: string; // e.g., 'proposed', 'approved_for_apply', etc.
  order_of_execution: number;
  executionLogs?: ExecutionLog[]; // Undefined until lazy-loaded, then an array
  files?: string;
  folders?: string;
  reason?: string;
  plain?: string; // For 'final' action's explanation text
  // Fields for MCP tool actions (action_type pattern: serverName__toolName)
  server_name?: string;
  tool_name?: string;
  arguments?: string; // Stringified JSON
  // Fields for FINAL with selections
  selections?: string; // JSON array of {question: string, selections: string[]}[]
}

export interface SessionInput {
  id: string;
  created_at: Date | string; // Allow string for ISO dates from API/Store
  updated_at: Date | string; // Allow string for ISO dates from API/Store
  session_id: string;
  user_prompt: string | null; // Allow null for carrier/placeholder inputs
  context_template_id: string | null;
  // contextTemplate?: ContextTemplate; // Assuming this might not be fully populated or needed everywhere
  ad_hoc_context_definition: string | null;
  generated_context_string: string;
  execution_strategy: string; // e.g., "review_first", "apply_revert"
  raw_llm_response: string | null;
  tool_calls?: string | null; // Stringified JSON of tool calls
  llm_response_explanation?: string | null;
  is_context_carrier?: boolean;
  is_discarded?: boolean; // Whether excluded from LLM context
  is_summary?: boolean; // Whether this is a synthetic summary replacing completed groups
  replaced_by_summary_input_id?: string | null; // UUID of summary that replaced this input
  aiActions: AIAction[];
  raw_studio_request_body?: string;
  input_token_count: number | null;
  output_token_count: number | null;
  cached_token_count: number | null;  // NEW
  error_message?: string | null; // NEW
  idempotency_key?: string | null;
  thoughts?: string | null;
  role?: string;
  external_conversation_id?: string | null;
  external_turn_id?: string | null;
  sequence_number?: number | null;
}
export enum SessionStatus {
  ACTIVE = 'active',
  COMPLETED = 'completed',
  ABORTED = 'aborted',
  FAILED = 'failed',
}

export type ReasoningEffort =
  | 'xhigh'
  | 'high'
  | 'medium'
  | 'low'
  | 'minimal'
  | 'none';

export const ALL_REASONING_EFFORTS: ReasoningEffort[] = [
  'xhigh',
  'high',
  'medium',
  'low',
  'minimal',
  'none',
];


export interface SystemPrompt {
  id: string;
  prompt_name: string;
  prompt_content: string;
  is_default: boolean;
  url: string | null;
  enabledTools: string[] | 'all' | null;
  followup_token_limit: number | null;
  is_builtin: boolean;
  builtin_key: string | null;
  created_at: Date;
  updated_at: Date;
}

export interface Session {
  id: string;
  created_at: Date;
  updated_at: Date;
  project_id: string | null;
  // project?: Project; // Assuming this might not be fully populated or needed everywhere
  session_title: string | null;
  end_time: Date | null;
  status: SessionStatus;
  system_prompt_id: string | null;
  systemPrompt?: SystemPrompt;
  default_initial_context_template_id: string | null;
  // defaultInitialContextTemplate?: ContextTemplate;
  default_followup_context_template_id: string | null;
  // defaultFollowupContextTemplate?: ContextTemplate;
  url: string | null;
  sessionInputs?: SessionInput[]; // Optional, as it might not always be loaded
  sessionInputCount?: number;
  model_id?: string | null;
  reasoning_effort?: ReasoningEffort | null;
  sub_agent_id: string | null;
  subAgent?: SubAgent;
  parent_session_id: string | null;
  parentSession?: Session;
  childSessions?: Session[];
  is_running?: boolean;
  latestInput?: {
    id: string;
    user_prompt: string | null;
    role: string | null;
    tool_calls: string | null;
    created_at: string;
  } | null;
}

// For: POST /sessions/{session_id}/inputs response
export interface SubmitPromptResponse {
  // Based on backend SessionInputsService.create response which returns a populated SessionInput
  session_input: SessionInput;
  // The 'actions' are part of session_input.aiActions, so no separate actions field needed here if SessionInput type is comprehensive.
}

// Types for batch action operation responses:

export interface ActionResult {
  action_id: string;
  status: string;
  logs: ExecutionLog[];
}

export interface ApplyApprovedActionsResponse {
  results: ActionResult[];
  message?: string; // Optional message, e.g., if no actions were processed
}

export interface DiscardedActionInfo {
  action_id: string;
  status: string;
}

export interface DiscardAllActionsResponse {
  results: DiscardedActionInfo[];
  message?: string; // Optional message
}

// New types for "Apply & Revert" batch operations
export interface ConfirmedActionInfo {
  action_id: string;
  status: string;
}

export interface ConfirmAllActionsResponse {
  results: ConfirmedActionInfo[];
  message?: string;
}

export interface RevertAllActionsResponse {
  results: ActionResult[]; // Similar to ApplyApprovedActionsResponse
  message?: string;
}

// Types for Context Template Management
export enum TemplateType {
  MAIN = 'main',
  PARTIAL = 'partial',
}

export interface ContextTemplate {
  id: string;
  template_name: string;
  template_content: string;
  context_definition: string | null;
  is_default_initial: boolean;
  is_default_followup: boolean;
  is_default_condensed: boolean;
  template_type: TemplateType;
  is_builtin: boolean;
  builtin_key: string | null;
  created_at: Date;
  updated_at: Date;
}

export interface CreateContextTemplatePayload {
  template_name: string;
  template_content: string;
  context_definition?: string;
  template_type?: TemplateType;
}

export interface UpdateContextTemplatePayload {
  template_name?: string;
  template_content?: string;
  context_definition?: string;
  template_type?: TemplateType;
}

export interface ContextDefinition {
  files?: string[];
  folders?: string[];
}

export interface PreviewContextTemplatePayload {
  template_content: string;
  context_definition?: ContextDefinition;
}

export interface PreviewContextTemplateResponse {
  rendered_context: string;
  token_count: number;
}

// New types for System Prompt Management
export interface CreateSystemPromptPayload {
  prompt_name: string;
  prompt_content: string;
  url?: string;
  followup_token_limit?: number | null;
}

export interface UpdateSystemPromptPayload {
  prompt_name?: string;
  prompt_content?: string;
  url?: string;
  followup_token_limit?: number | null;
}

export interface PreviewSystemPromptPayload {
  prompt_content: string;
}

export interface PreviewSystemPromptResponse {
  rendered_content: string;
}

export interface UpdateSessionPayload {
  session_title?: string | null;
  status?: SessionStatus;
  system_prompt_id?: string | null;
  default_initial_context_template_id?: string | null;
  default_followup_context_template_id?: string | null;
  url?: string | null;
  model_id?: string | null;
  reasoning_effort?: ReasoningEffort | null;
}

export interface CreateSessionFromContextPayload {
  session_title?: string;
}

// Workspace types
export interface TreeNode {
  id: string;
  name: string;
  children?: TreeNode[];
}

export interface WorkspaceFileTreeResponse {
  tree: TreeNode[];
}

export interface SearchResultItem {
  type: 'file' | 'folder';
  path: string;
}

export interface SearchWorkspaceResponse {
  results: SearchResultItem[];
  total: number;
  page: number;
  hasMore: boolean;
}

export interface WorkspaceFileContentResponse {
  content: string;
}

// Custom Snippet types
export interface CustomSnippet {
  id: string;
  prefix: string;
  description: string;
  body: string;
  scope?: string;
  created_at: Date;
  updated_at: Date;
}

export interface CreateCustomSnippetPayload {
  prefix: string;
  description: string;
  body: string;
  scope?: string;
}

export interface UpdateCustomSnippetPayload {
  prefix?: string;
  description?: string;
  body?: string;
  scope?: string;
}

// Context Snippet types
export interface ContextSnippet {
    id: string;
    handle: string;
    description: string | null;
    template_content: string;
    created_at: Date;
    updated_at: Date;
}

export interface CreateContextSnippetPayload {
    handle: string;
    description?: string;
    template_content: string;
}

export interface UpdateContextSnippetPayload {
    handle?: string;
    description?: string;
    template_content?: string;
}

export interface PreviewContextSnippetPayload {
    template_content: string;
}

export interface PreviewContextSnippetResponse {
    rendered_text: string;
    error?: string;
}


// App Info
export interface AppInfo {
  project_path: string;
  port: number;
  is_development: boolean;
}

// Session Import/Export
export interface ImportSessionResponse {
  message: string;
  session_id: string;
  message_count: number;
}

// Orchestration Types
export interface OrchestrationScript {
  name: string;
  content: string;
}

export interface OrchestrationScriptDetail {
  content: string;
}

export interface CreateOrchestrationScriptPayload {
  name: string;
  content: string;
}

export interface UpdateOrchestrationScriptPayload {
  content: string;
}

export type OrchestrationStatus =
  | 'idle'
  | 'running'
  | 'paused_interactive'
  | 'waiting_for_llm_response'
  | 'stopping'
  | 'stopped'
  | 'failed'
  | 'completed';

export interface OrchestrationRunState {
  runId: string;
  status: OrchestrationStatus;
  mode: 'auto' | 'interactive';
  scriptName: string;
  sessionId: string;
  currentStep: number;
  totalSteps: number;
  waitStartTime: number | null;
  waitingOnInputId: string | null;
  error: string | null;
  log: string[];
  timeoutMs: number;
}

// MCP (Model Context Protocol)
export interface McpToolSchemaProperty {
  type: string;
  description: string;
  enum?: string[];
}

export interface McpToolSchema {
  type: 'object';
  properties: Record<string, McpToolSchemaProperty>;
  required?: string[];
}

export interface McpTool {
  tool_name: string;
  description: string;
  input_schema: McpToolSchema;
  is_active: boolean;
}

export interface McpServer {
  server_name: string;
  description: string;
  is_active: boolean;
  tools: McpTool[];
  transport_type?: 'stdio' | 'streamable_http';
  logo_url?: string | null;
  category?: string | null;
  marketplace_id?: string | null;
  token_status?: 'valid' | 'expired' | 'unknown' | 'none';
}

/**
 * Descriptor for the built-in Repoburg MCP server (exposes config-management
 * tools over stdio/streamableHttp). Unlike external `McpServer`s, it is not
 * user-created or deletable; the UI displays it read-only with a copy-paste
 * client config plus a start/stop control for the automated streamableHttp
 * instance.
 */
export interface RepoburgMcpServer {
  server_name: string;
  description: string;
  is_builtin: true;
  /** Whether the automated (backend-spawned) streamableHttp server is running. */
  is_running: boolean;
  /** Client URL of the running server, e.g. http://127.0.0.1:3100/mcp. */
  client_url: string;
  /** Pretty-printed JSON client config ready to paste into an MCP client. */
  client_config: string;
  tools: McpTool[];
}

/**
 * Instance listed by the daemon MCP gateway — mirrors the daemon's `Service`
 * type (name, path, port, status).
 */
export interface RepoburgMcpGatewayInstance {
  name: string;
  path: string;
  port: number;
  status: 'running' | 'stopped' | 'errored';
}

/**
 * Tool exposed by the daemon MCP gateway. The `manage_*` tools have an
 * `instance_name` required parameter added by the gateway; daemon-level tools
 * (`list_instances`, `start_instance`, `stop_instance`) do not.
 */
export interface McpGatewayToolSchemaProperty {
  type: string;
  description: string;
  enum?: string[];
}

export interface RepoburgMcpGatewayTool {
  name: string;
  description: string;
  inputSchema: {
    type: 'object';
    properties: Record<string, McpGatewayToolSchemaProperty>;
    required?: string[];
  };
}

/**
 * Descriptor for the daemon-hosted MCP gateway — a single MCP entry point at
 * `http://localhost:9998/mcp` that manages all Repoburg instances. The MCP
 * client connects once and sends `instance_name` with each `manage_*` call.
 */
export interface RepoburgMcpGateway {
  server_name: string;
  description: string;
  /** Gateway URL, e.g. http://localhost:9998/mcp. */
  url: string;
  /** All known Repoburg instances (running or stopped). */
  instances: RepoburgMcpGatewayInstance[];
  /** Daemon-level tools + cached manage_* tools (with instance_name). */
  tools: RepoburgMcpGatewayTool[];
  /** Pretty-printed JSON client config ready to paste into an MCP client. */
  client_config: string;
}

// Custom Variable types
export interface CustomVariable {
    id: string;
    key: string;
    value: string;
    enabled: boolean;
    is_freq_used: boolean;
    order_number: number;
    created_at: Date;
    updated_at: Date;
}

export interface CreateCustomVariablePayload {
    key: string;
    value: string;
    enabled?: boolean;
    is_freq_used?: boolean;
    order_number?: number;
}

export interface UpdateCustomVariablePayload {
    key?: string;
    value?: string;
    enabled?: boolean;
    is_freq_used?: boolean;
    order_number?: number;
}

// Sub-Agent types
export interface SubAgent {
  id: string;
  created_at: Date;
  updated_at: Date;
  name: string;
  description: string | null;
  system_prompt_id: string | null;
  context_template_id: string | null;
  followup_context_template_id: string | null;
  enabled_tools: string[] | null;
  is_active: boolean;
  is_builtin: boolean;
  builtin_key: string | null;
  model_id: string | null;
  systemPrompt?: {
    id: string;
    prompt_name: string;
    prompt_content: string;
    enabledTools: string[] | 'all' | null;
  };
  contextTemplate?: {
    id: string;
    template_name: string;
    template_content: string;
  };
  followupContextTemplate?: {
    id: string;
    template_name: string;
    template_content: string;
  };
}

// Agent types
export interface Agent {
  id: string;
  created_at: Date;
  updated_at: Date;
  name: string;
  description: string | null;
  system_prompt_id: string | null;
  context_template_id: string | null;
  followup_context_template_id: string | null;
  model_id: string | null;
  reasoning_effort: ReasoningEffort | null;
  is_default: boolean;
  is_active: boolean;
  is_builtin: boolean;
  builtin_key: string | null;
  systemPrompt?: {
    id: string;
    prompt_name: string;
    prompt_content: string;
  };
  contextTemplate?: {
    id: string;
    template_name: string;
  };
  followupContextTemplate?: {
    id: string;
    template_name: string;
  };
}



// Daemon Instance types
export interface InstanceService {
  name: string;
  path: string;
  port: number;
  status: 'running' | 'stopped' | 'errored';
  pid: number | null;
}

// Global Config types
export type SettingSource = 'project' | 'global' | 'default';

export interface SettingWithSource<T> {
  value: T;
  source: SettingSource;
}

export interface GlobalConfig {
  executionStrategy?: string;
  websocketEnabled?: boolean;
  autoContextFetchEnabled?: boolean;
  yoloModeEnabled?: boolean;
  yoloModeMessage?: string;
  streamingEnabled?: boolean;
  toolsEnabled?: boolean;
  syntaxValidationEnabled?: boolean;
  historyCompressionEnabled?: boolean;
  llmRetryEnabled?: boolean;
  llmRetryMaxAttempts?: number;
  contextTokenLimit?: number;
  followupTokenLimit?: number;
  notificationsEnabled?: boolean;
  openrouterApiKey?: string;
  zaiApiKey?: string;
  alibabaApiKey?: string;
  ollamaApiKey?: string;
  openaiApiKey?: string;
  openaiBaseUrl?: string;
}

export interface EffectiveSettings {
  executionStrategy: SettingWithSource<string>;
  websocketEnabled: SettingWithSource<boolean>;
  autoContextFetchEnabled: SettingWithSource<boolean>;
  yoloModeEnabled: SettingWithSource<boolean>;
  yoloModeMessage: SettingWithSource<string>;
  streamingEnabled: SettingWithSource<boolean>;
  toolsEnabled: SettingWithSource<boolean>;
  syntaxValidationEnabled: SettingWithSource<boolean>;
  historyCompressionEnabled: SettingWithSource<boolean>;
  llmRetryEnabled: SettingWithSource<boolean>;
  llmRetryMaxAttempts: SettingWithSource<number>;
  notificationsEnabled: SettingWithSource<boolean>;
  contextTokenLimit: SettingWithSource<number>;
  followupTokenLimit: SettingWithSource<number>;
  openrouterApiKey: SettingWithSource<string | null>;
  zaiApiKey: SettingWithSource<string | null>;
  alibabaApiKey: SettingWithSource<string | null>;
  ollamaApiKey: SettingWithSource<string | null>;
  openaiApiKey: SettingWithSource<string | null>;
  openaiBaseUrl: SettingWithSource<string | null>;
}

// MCP Marketplace types

/**
 * A remote MCP server listed in the marketplace catalog.
 * Each server uses OAuth (authorization code + PKCE) via Streamable HTTP transport.
 */
export interface MarketplaceServer {
  /** Unique slug identifier (e.g. "github") */
  id: string;
  /** Display name */
  name: string;
  /** Short description */
  description: string;
  /** Remote MCP server endpoint URL */
  serverUrl: string;
  /** Logo URL for card display */
  logoUrl: string;
  /** Category for grouping/filtering */
  category: string;
  /** Authentication type — all featured servers use OAuth */
  authType: 'oauth';
}

/** Response from POST /mcp/marketplace/connect */
export interface InitiateConnectResponse {
  authUrl: string;
  state: string;
}

/** Response from GET /mcp/marketplace/connect/status/:id */
export interface ConnectStatusResponse {
  status: 'pending' | 'connected' | 'error';
  error?: string;
}